import {IError, ISagaAction} from "modules/types";
import {Api, ApiError} from "modules/utils/Api";
import {SagaIterator} from "redux-saga";
import {call, put} from "typed-redux-saga";
import {
	IBackdoorPayload,
	ILoginAppPayload,
	ILoginPayload,
	IRegisterAppPayload,
	IRegisterPayload,
	IUpdatePayload,
	showGlobalError,
	userAskForRegistration,
	userAskForRegistrationApp,
	userChecked,
	userFetchFailed,
	userFetchSuccess,
	userLoginFailed,
	userLoginSuccess,
	userLogout,
	userLogoutFailed,
	userLogoutSuccess,
	userRegisterAllFields,
	userRegisterAllFieldsApp,
	userRegisterFailed,
	userRegisterSuccess,
	userUpdateFailed,
	userUpdateSuccess,
} from "modules/actions";
import {User} from "modules/utils";
import {FAN_SSO_ENABLED} from "modules/constants";

export const userRegisterSaga = function* ({payload}: ISagaAction<IRegisterPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.register, payload);
		yield* put(userRegisterSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as Error;
		yield* put(userRegisterFailed(message));
		yield* put(showGlobalError(err as Error));
	} finally {
		yield* put(userChecked());
	}
};

export const userRegisterAppSaga = function* ({
	payload,
}: ISagaAction<IRegisterAppPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.registerApp, payload);
		yield* put(userRegisterSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as Error;
		yield* put(userRegisterFailed(message));
		yield* put(showGlobalError(err as Error));
	} finally {
		yield* put(userChecked());
	}
};

export const userRegisterAllFieldsSaga = function* ({
	payload,
}: ISagaAction<ILoginPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.allFieldsRegister, payload);
		yield* put(userRegisterSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as Error;
		yield* put(userRegisterFailed(message));
		yield* put(showGlobalError(err as Error));
	} finally {
		yield* put(userChecked());
	}
};

export const userRegisterAllFieldsAppSaga = function* ({
	payload,
}: ISagaAction<ILoginAppPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.allFieldsRegisterApp, payload);
		yield* put(userRegisterSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as Error;
		yield* put(userRegisterFailed(message));
		yield* put(showGlobalError(err as Error));
	} finally {
		yield* put(userChecked());
	}
};

export const userLoginSaga = function* ({payload}: ISagaAction<ILoginPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.login, payload);
		yield* put(userLoginSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message, code} = err as IError;
		if (code === ApiError.USER_NOT_REGISTERED && FAN_SSO_ENABLED) {
			yield* put(userRegisterAllFields(payload));
		} else if (code === ApiError.USER_NOT_REGISTERED) {
			yield* put(userAskForRegistration(payload.token));
		} else {
			yield* put(showGlobalError(err as Error));
		}
		yield* put(userLoginFailed(message));
	} finally {
		yield* put(userChecked());
	}
};

export const userBackdoorLoginSaga = function* ({
	payload,
}: ISagaAction<IBackdoorPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.backdoor, payload);
		yield* put(userLoginSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as IError;
		yield* put(showGlobalError(err as Error));
		yield* put(userLoginFailed(message));
	}
};

export const userLoginAppSaga = function* ({payload}: ISagaAction<ILoginAppPayload>): SagaIterator {
	try {
		const response = yield* call(Api.Auth.loginApp, payload);
		yield* put(userLoginSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message, code} = err as IError;
		if (code === ApiError.USER_NOT_REGISTERED && FAN_SSO_ENABLED) {
			yield* put(userRegisterAllFieldsApp(payload));
		} else if (code === ApiError.USER_NOT_REGISTERED) {
			yield* put(userAskForRegistrationApp(payload));
		} else {
			yield* put(showGlobalError(err as Error));
		}
		yield* put(userLoginFailed(message));
	} finally {
		yield* put(userChecked());
	}
};

export const userUpdateSaga = function* ({payload}: ISagaAction<IUpdatePayload>): SagaIterator {
	try {
		const response = yield* call(Api.User.update, payload);
		yield* put(userUpdateSuccess(response.success.user));
		User.SAVE(response.success.user);
	} catch (err) {
		const {message} = err as Error;
		yield* put(userUpdateFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const userLogoutSaga = function* (): SagaIterator {
	try {
		yield* call(Api.Auth.logout);
		yield* put(userLogoutSuccess());
		User.CLEAR();
	} catch (err) {
		const {message} = err as Error;
		yield* put(userLogoutFailed(message));
	}
};

export const userFetchSaga = function* (): SagaIterator {
	try {
		const response = yield* call(Api.User.user);
		const user = response.success.user;
		if (user.isSSO) {
			yield* put(userFetchSuccess(response.success.user));
			User.SAVE(response.success.user);
		} else {
			yield* put(userFetchFailed("User not registered with SSO"));
			yield* put(userLogout());
		}
	} catch (err) {
		const {message} = err as Error;
		yield* put(userFetchFailed(message));
	} finally {
		yield* put(userChecked());
	}
};
